import { useGetAllAssetInsights } from "@/api/studio/insights/queries";
import { ReportsLayout } from "@/components/layouts";
import { Card, CardBody, CardSkeleton } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Table, TableHeader, TableRow, TableCell } from "@/components/ui/table";
import { Metric } from "@/features/reports/components/metric";
import { ReportBarChart } from "@/features/reports/components/report-bar-chart";
import { ReportNavToolbar } from "@/features/reports/components/report-nav-toolbar";
import { useReportsContext } from "@/features/reports/contexts/reports-context";
import { useReportFiltersStore } from "@/features/reports/stores/use-report-filters-store";
import { getThumbnailImage } from "@/features/reports/utils/get-thumbnail-image";
import { getTopScore } from "@/features/reports/utils/get-top-score";
import { useSegment } from "@/hooks/use-segment";
import { useLocation } from "wouter";
import { Bars3BottomLeftIcon, PhotoIcon, ScaleIcon } from "@heroicons/react/20/solid";
import { MemoryEmotionMetric } from "@/features/reports/components/memory-emotion-metric";
import { useUpdateRoute } from "@/features/studies/hooks/use-update-route";
import { MemoryEmotionIndicator } from "@/components/ui/indicator/memory-emotion-indicator";
import { EmotionScore } from "@/api/studio/insights/types/emotion.type";
import { useEffect } from "react";
import { useGlobalStore } from "@/stores/global-store";
import { EntitlementGate } from "@/features/auth/components/entitlement-gate";
import { PadlockIcon } from "@/assets/icons";
import { useModalStore } from "@/stores/use-modal-store";
import { EmotionUpsellModal } from "@/features/reports/modals/emotion-upsell-modal";

/* Prop Types */
interface ReportsEmotionRouteProps {
  projectId: string;
  studyId: string;
}

/*
 * ReportsEmotionRoute component
 *
 * Route for the Emotion report page
 * Displays a list of assets with their emotion metrics
 * Clicking on an asset navigates to the in-depth report for that asset
 */
export const ReportsEmotionRoute = ({ projectId, studyId }: ReportsEmotionRouteProps) => {
  /* Stores */
  const sortBy = useReportFiltersStore((state) => state.sortBy);
  const orderBy = useReportFiltersStore((state) => state.orderBy);
  const theme = useGlobalStore((state) => state.theme);
  const entitlements = useGlobalStore((state) => state.entitlements);
  const { open } = useModalStore((state) => state.modalActions);
  const [, navigate] = useLocation();

  /* Context */
  const {
    state: { view },
    actions: { setReportType, setAssetKey },
  } = useReportsContext();

  // Check if user has access
  const hasMemoryEmotion = entitlements?.some((item) => item.name === "memoryEmotion");

  /* Queries */
  const { data: allInsights, isLoading: isAllInsightsLoading } = useGetAllAssetInsights({ projectId, studyId, orderBy, sortBy });
  /* Hooks */
  const { segmentTrackEvent, segmentPageEvent } = useSegment();
  // Update the route with the current step
  useUpdateRoute(projectId, studyId, "reports/emotion/overview");

  /* Handlers */
  const handleClick = (assetId: string) => {
    // segment track event when a user clicks on a report asset
    segmentTrackEvent("Asset Report Viewed", {
      studyId,
      projectId,
      assetId,
      reportType: "emotion",
      studyType: "asset",
    });
    // set the report type and assetKey
    setReportType("emotion");
    setAssetKey(assetId);
    // navigate to the in-depth report page for the selected asset
    navigate(`/projects/${projectId}/studies/${studyId}/asset/reports/in-depth`);
  };

  const handleEmotionUpsellModal = () => {
    open(<EmotionUpsellModal />);
  };

  /* Side effects */
  useEffect(() => {
    // segment page event when a user visits the reports overview page
    if (theme)
      segmentPageEvent("Study Report", {
        title: "Emotion Overview Report | Dragonfly AI Studio",
        theme: theme,
      });
  }, []);

  // Show upsell modal on load if user doesn't have access
  useEffect(() => {
    if (!hasMemoryEmotion && entitlements.length > 0) {
      handleEmotionUpsellModal();
    }
  }, [hasMemoryEmotion, entitlements]);

  /* Render */
  return (
    <ReportsLayout projectId={projectId} studyId={studyId}>
      {/* Navbar */}
      <ReportNavToolbar projectId={projectId} studyId={studyId} currentTab="emotion" hasContext={false} showFilters={hasMemoryEmotion} />
      {/* Body */}
      <div className="grow flex flex-row gap-4 overflow-hidden">
        <div className="grow flex flex-col gap-4 overflow-y-auto">
          {/* Cards */}
          {view === "Cards" && (
            <div className="flex flex-row gap-4 flex-wrap">
              {/* Loading */}
              {isAllInsightsLoading && Array.from({ length: 4 }).map((_, index) => <CardSkeleton key={index} />)}

              {/* Insights */}
              {allInsights &&
                allInsights.map((insight, index) => (
                  <Card
                    key={insight.key}
                    onClick={() => (!hasMemoryEmotion ? handleEmotionUpsellModal() : handleClick(insight.key))}
                    imageUrl={getThumbnailImage(insight.key, 300, 300, projectId, studyId)}
                    imgFit="contain"
                    winner={hasMemoryEmotion && getTopScore(index, allInsights.length, sortBy)}
                  >
                    <EntitlementGate
                      entitlement="memoryEmotion"
                      fallback={
                        <CardBody className="flex flex-col gap-5 px-4 py-5">
                          <div className="flex flex-col gap-2 truncate">
                            <p className="text-xs truncate">{insight.assetName}</p>
                            <div className="flex flex-row items-center justify-between text-base font-semibold">
                              <span>Emotion</span>
                              <div className="flex flex-row items-center gap-5 justify-end">
                                <div className="w-12 h-1 bg-base-200 rounded-full"></div>
                                <div className="flex flex-row items-center py-1.5 px-2 bg-base-200 rounded-lg">
                                  <PadlockIcon isGradient={true} />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col gap-3 w-full">
                            <div className="grid grid-cols-2 justify-between text-xs items-center gap-2">
                              <div className="flex flex-row items-center gap-2">
                                <ScaleIcon className="size-4 opacity-60" />
                                <span>Reaction</span>
                              </div>
                              <div className="flex flex-row items-center justify-end gap-5">
                                <div className="w-12 h-1 bg-base-200 rounded-full"></div>
                                <div className="flex flex-row items-center py-1 px-1.5 bg-base-200 rounded-lg">
                                  <PadlockIcon isGradient={true} />
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 justify-between text-xs items-center gap-2">
                              <div className="flex flex-row items-center gap-2">
                                <PhotoIcon className="size-4 opacity-60" />
                                <span>Visuals</span>
                              </div>
                              <div className="flex flex-row items-center justify-end gap-5">
                                <div className="w-12 h-1 bg-base-200 rounded-full"></div>
                                <div className="flex flex-row items-center py-1 px-1.5 bg-base-200 rounded-lg">
                                  <PadlockIcon isGradient={true} />
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 justify-between text-xs items-center gap-2">
                              <div className="flex flex-row items-center gap-2">
                                <Bars3BottomLeftIcon className="size-4 opacity-60" />
                                <span>Text</span>
                              </div>
                              <div className="flex flex-row items-center justify-end gap-5">
                                <div className="w-12 h-1 bg-base-200 rounded-full"></div>
                                <div className="flex flex-row items-center py-1 px-1.5 bg-base-200 rounded-lg">
                                  <PadlockIcon isGradient={true} />
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardBody>
                      }
                    >
                      <CardBody className="flex flex-col gap-5 px-4 py-5">
                        <div className="flex flex-col gap-2 truncate">
                          <p className="text-xs truncate">{insight.assetName}</p>
                          <Metric title="Emotion" className="text-base font-semibold" value={Math.round(insight.metrics.emotion?.score ?? 0)} />
                        </div>

                        <div className="flex flex-col gap-3">
                          <MemoryEmotionMetric
                            title="Reaction"
                            size="small"
                            textSize="xs"
                            value={insight.metrics.emotion?.reaction}
                            icon={<ScaleIcon className="size-4" />}
                            type="reaction"
                          />
                          <MemoryEmotionMetric
                            title="Visuals"
                            size="small"
                            textSize="xs"
                            value={insight.metrics.emotion?.visual}
                            icon={<PhotoIcon className="size-4" />}
                            type="emotion"
                          />
                          <MemoryEmotionMetric
                            title="Text"
                            size="small"
                            textSize="xs"
                            value={insight.metrics.emotion?.text}
                            icon={<Bars3BottomLeftIcon className="size-4" />}
                            type="emotion"
                          />
                        </div>
                      </CardBody>
                    </EntitlementGate>
                  </Card>
                ))}
            </div>
          )}

          {/* Chart */}
          {view === "Chart" && (
            <>
              <div className="max-h-80 min-h-80 h-80 grow border-b border-base-300">
                {allInsights && <ReportBarChart insightData={allInsights} projectId={projectId} studyId={studyId} variant="emotion" />}
              </div>
              <div className="flex-1 flex flex-col gap-4">
                <Table>
                  <thead>
                    <TableRow>
                      <TableHeader>Asset</TableHeader>
                      <TableHeader className="w-40">Ranking</TableHeader>
                      <TableHeader className="w-56">
                        <div className="flex flex-row items-center gap-3">
                          <span className="flex w-4 h-4 rounded-sm bg-primary"></span>
                          Emotion
                        </div>
                      </TableHeader>
                      <TableHeader className="w-56">
                        <div className="flex flex-row items-center gap-3">
                          <ScaleIcon className="size-4" />
                          Reaction
                        </div>
                      </TableHeader>
                      <TableHeader className="w-56">
                        <div className="flex flex-row items-center gap-3">
                          <PhotoIcon className="size-4" />
                          Visuals
                        </div>
                      </TableHeader>
                      <TableHeader className="w-56">
                        <div className="flex flex-row items-center gap-3">
                          <Bars3BottomLeftIcon className="size-4" />
                          Text
                        </div>
                      </TableHeader>
                    </TableRow>
                  </thead>
                  <tbody>
                    {allInsights &&
                      allInsights.map((insight, index) => (
                        <TableRow key={insight.key} onClick={() => handleClick(insight.key)}>
                          <TableCell className="truncate">
                            <img src={getThumbnailImage(insight.key, 150, 150, projectId, studyId)} className="rounded-lg w-12 h-12 object-cover shrink-0" />
                            <small className="truncate">{insight.assetName}</small>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-row items-center gap-2">
                              {sortBy === "asc" ? allInsights.length - index : index + 1}
                              {getTopScore(index, allInsights.length, sortBy) && <div className="bg-pink-500 text-white px-2 py-1 rounded-lg text-xs font-medium">Top score</div>}
                            </div>
                          </TableCell>
                          <TableCell>{Math.round(insight.metrics.emotion?.score ?? 0)}%</TableCell>
                          <TableCell>
                            <MemoryEmotionIndicator size="small" score={insight.metrics.emotion?.reaction} type="reaction" />
                          </TableCell>
                          <TableCell>
                            <MemoryEmotionIndicator size="small" score={insight.metrics.emotion?.visual as EmotionScore} type="emotion" />
                          </TableCell>
                          <TableCell>
                            <MemoryEmotionIndicator size="small" score={insight.metrics.emotion?.text as EmotionScore} type="emotion" />
                          </TableCell>
                        </TableRow>
                      ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {/* List */}
          {view === "List" && allInsights && (
            <div className="flex-1 flex flex-col gap-4">
              <Table>
                <thead>
                  <TableRow>
                    <TableHeader>Assets</TableHeader>
                    <TableHeader className="w-40">Ranking</TableHeader>
                    <TableHeader className="w-60">Emotion</TableHeader>
                    <TableHeader className="w-60">Reaction</TableHeader>
                    <TableHeader className="w-60">Visuals</TableHeader>
                    <TableHeader className="w-60">Text</TableHeader>
                  </TableRow>
                </thead>
                <tbody>
                  {allInsights.map((insight, index) => (
                    <TableRow key={insight.key} onClick={() => handleClick(insight.key)}>
                      <TableCell className="truncate">
                        <img src={getThumbnailImage(insight.key, 150, 150, projectId, studyId)} className="rounded-lg w-12 h-12 object-cover shrink-0" />
                        <small className="truncate">{insight.assetName}</small>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-row items-center gap-2">
                          {sortBy === "asc" ? allInsights.length - index : index + 1}
                          {getTopScore(index, allInsights.length, sortBy) && <div className="bg-pink-500 text-white px-2 py-1 rounded-lg text-xs font-medium">Top score</div>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Progress value={Math.round(insight.metrics.emotion?.score ?? 0)} className="w-20" />
                        {Math.round(insight.metrics.emotion?.score ?? 0)}%
                      </TableCell>
                      <TableCell>
                        <MemoryEmotionIndicator score={insight.metrics.emotion?.reaction} size="small" type="reaction" />
                      </TableCell>
                      <TableCell>
                        <MemoryEmotionIndicator score={insight.metrics.emotion?.visual} size="small" type="emotion" />
                      </TableCell>
                      <TableCell>
                        <MemoryEmotionIndicator score={insight.metrics.emotion?.text} size="small" type="emotion" />
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </ReportsLayout>
  );
};
